"use client";

import type { MouseEvent } from "react";
import type {
  AdvancedCanvasLegacyDataAttributes,
  AdvancedCanvasRect,
  AdvancedSlashCommandItem,
} from "./types";

export type AdvancedSlashCommandMenuProps = {
  activeIndex: number;
  items: readonly AdvancedSlashCommandItem[];
  legacyDataAttributes?: AdvancedCanvasLegacyDataAttributes;
  noResultsLabel: string;
  onSelect: (id: string) => void;
  rect: AdvancedCanvasRect;
};

export function AdvancedSlashCommandMenu({
  activeIndex,
  items,
  legacyDataAttributes,
  noResultsLabel,
  onSelect,
  rect,
}: AdvancedSlashCommandMenuProps) {
  function preventEditorBlur(event: MouseEvent<HTMLElement>) {
    event.preventDefault();
  }

  return (
    <div
      className="pointer-events-auto absolute z-[70] w-[min(20rem,calc(100%-1rem))] overflow-hidden rounded-md border border-slate-200 bg-white text-sm text-slate-900 shadow-[0_18px_48px_rgba(15,23,42,0.22)]"
      {...legacyDataAttributes}
      data-builder-editor-ui="true"
      data-builder-command-menu="slash"
      style={{
        left: rect.left,
        top: rect.top + rect.height + 8,
      }}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={preventEditorBlur}
    >
      {items.length === 0 ? (
        <div className="px-3 py-3 text-xs text-slate-500">
          {noResultsLabel}
        </div>
      ) : items.map((item, index) => {
        const active = index === activeIndex;

        return (
          <button
            key={item.id}
            type="button"
            className={[
              "flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors",
              active
                ? "bg-teal-50 text-slate-950"
                : "hover:bg-slate-50",
            ].join(" ")}
            aria-label={item.label}
            data-builder-command-item={item.id}
            data-builder-command-item-active={active ? "true" : "false"}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onSelect(item.id);
            }}
          >
            <span
              className={[
                "mt-0.5 inline-flex h-7 min-w-7 items-center justify-center rounded border px-1.5 font-mono text-[11px] font-semibold",
                active
                  ? "border-[#00D7FF] bg-white text-slate-950 shadow-[0_0_0_2px_rgba(0,215,255,0.18)]"
                  : "border-slate-200 bg-slate-50 text-slate-500",
              ].join(" ")}
            >
              {item.badge}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">
                {item.label}
              </span>
              {item.description ? (
                <span className="mt-0.5 block truncate text-xs text-slate-500">
                  {item.description}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
